/**
 * ProgressGlance (iter159) — Quick-glance student progress card.
 *
 * Default view (visible on load):
 *   · Current spider chart (from latest post-session snapshot)
 *   · Latest session summary — clamped to 2-3 lines
 *   · Top 3 current focus areas as bulleted list
 *   · "Book Your Next Focus Session" primary CTA (deep-links to
 *     /group-bookings/new?type=solo&coach={id}&focus={a,b})
 *
 * Expanded view (revealed by tapping "See More"):
 *   · Full timeline: per-session card with summary + focus chips +
 *     per-skill notes + a per-session spider snapshot
 *   · "Bring a Training Partner" secondary CTA (Group flow with the
 *     same focus areas prefilled)
 *
 * Uses two endpoints:
 *   GET /students/{id}/skill-snapshots     (iter152) — spider data
 *   GET /students/{id}/post-session-feedback (iter156) — per-skill notes
 *     and coach_id for the deep-link CTAs
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { CalendarClock, ChevronDown, ChevronUp, Users, Sparkles, ArrowUp, ArrowDown, Minus, BookOpen, ArrowRight, Plus, Check, Heart } from "lucide-react";
import { toast } from "sonner";

// ---- Spider chart (compact, same math as StudentSpiderTimeline) ----------
function Spider({ snapshot = {}, size = 220, color = "#CCFF00" }) {
  const skills = Object.keys(snapshot).slice(0, 8);
  if (skills.length < 3) {
    return (
      <div
        style={{ width: size, height: size }}
        className="flex items-center justify-center text-[11px] text-[#525252] border border-[#262626] rounded-full"
        data-testid="progress-glance-spider-empty"
      >
        No snapshot yet
      </div>
    );
  }
  const cx = size / 2, cy = size / 2, r = size / 2 - 22;
  const step = (Math.PI * 2) / skills.length;
  const points = skills.map((k, i) => {
    const val = Math.max(0, Math.min(5, Number(snapshot[k]) || 0)) / 5;
    const a = -Math.PI / 2 + i * step;
    return [cx + Math.cos(a) * r * val, cy + Math.sin(a) * r * val];
  });
  const rings = [0.25, 0.5, 0.75, 1];
  const poly = points.map((p) => p.join(",")).join(" ");
  return (
    <svg width={size} height={size} data-testid="progress-glance-spider">
      {rings.map((ring, i) => (
        <polygon key={i} points={skills.map((_, j) => {
          const a = -Math.PI / 2 + j * step;
          return `${cx + Math.cos(a) * r * ring},${cy + Math.sin(a) * r * ring}`;
        }).join(" ")} fill="none" stroke="#262626" strokeWidth="1" />
      ))}
      {skills.map((k, i) => {
        const a = -Math.PI / 2 + i * step;
        const lx = cx + Math.cos(a) * (r + 12);
        const ly = cy + Math.sin(a) * (r + 12);
        return (
          <text key={k} x={lx} y={ly} fontSize="9" fill="#A3A3A3"
            textAnchor="middle" dominantBaseline="middle" style={{ textTransform: "uppercase", letterSpacing: "0.08em" }}>
            {k.replace(/_/g, " ")}
          </text>
        );
      })}
      <polygon points={poly} fill={color + "33"} stroke={color} strokeWidth="1.5" />
    </svg>
  );
}

const humanise = (slug) =>
  String(slug || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch { return String(iso).slice(0, 10); }
};

// Iter160 — Trend arrow next to each focus bullet.
//
// The focus bullets are free-text AI sentences (e.g. "Reduce wrist movement
// on dink shots"), whereas the snapshot map uses slugs (`dinks`, `footwork`,
// `third_shot_drop`, …). We match by substring on the humanised slug so
// "dink" hits `dinks`, "footwork" hits `footwork`, etc. Delta is measured
// between the LATEST snapshot value and the mean of the two prior sessions
// for the same key. Only 2+ data points per skill trigger an arrow — a
// single session shows the neutral placeholder so the student isn't misled.
const TREND_UP = 0.2;    // volt green — clear improvement
const TREND_DOWN = -0.2;  // orange — regression

function detectTrend(focusText, snapshots) {
  if (!focusText || !Array.isArray(snapshots) || snapshots.length === 0) return null;
  const text = String(focusText).toLowerCase();
  const keys = new Set();
  for (const s of snapshots) {
    for (const k of Object.keys(s.snapshot || {})) keys.add(k);
  }
  // Find the snapshot key that appears in the focus bullet text.
  // Prefer longer/more specific slug matches (e.g. "third_shot_drop" before "shot").
  const sorted = [...keys].sort((a, b) => b.length - a.length);
  let matchedKey = null;
  for (const k of sorted) {
    const humanised = k.replace(/_/g, " ").toLowerCase();
    // Also try the stem (drop trailing plural s) so "dinks" hits "dink".
    const stem = humanised.endsWith("s") ? humanised.slice(0, -1) : humanised;
    if (text.includes(humanised) || (stem.length > 3 && text.includes(stem))) {
      matchedKey = k;
      break;
    }
  }
  if (!matchedKey) return null;
  // Snapshots come newest-first. Read values in order.
  const values = snapshots
    .map((s) => Number(s.snapshot?.[matchedKey]))
    .filter((v) => Number.isFinite(v));
  if (values.length < 2) return { key: matchedKey, direction: "flat", latest: values[0], baseline: null };
  const latest = values[0];
  const priorSlice = values.slice(1, 3); // up to 2 prior sessions
  const baseline = priorSlice.reduce((a, b) => a + b, 0) / priorSlice.length;
  const delta = latest - baseline;
  const direction = delta > TREND_UP ? "up" : delta < TREND_DOWN ? "down" : "flat";
  return { key: matchedKey, direction, latest, baseline, delta };
}

function TrendArrow({ trend }) {
  if (!trend) return null;
  const { direction, latest, baseline, delta } = trend;
  const label = (() => {
    if (direction === "up") return `Trending up · ${latest?.toFixed(1)} vs ${baseline?.toFixed(1)}`;
    if (direction === "down") return `Regressing · ${latest?.toFixed(1)} vs ${baseline?.toFixed(1)}`;
    if (baseline == null) return "Baseline session — a trend will appear once you have 2 snapshots";
    return `Holding steady · ${latest?.toFixed(1)} vs ${baseline?.toFixed(1)}`;
  })();
  const color = direction === "up" ? "#CCFF00" : direction === "down" ? "#FF4500" : "#525252";
  const Icon = direction === "up" ? ArrowUp : direction === "down" ? ArrowDown : Minus;
  return (
    <span
      className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider shrink-0"
      data-testid={`focus-trend-${direction}`}
      title={label}
      aria-label={label}
      style={{ color }}
    >
      <Icon className="w-3 h-3" strokeWidth={2.5} />
      {Number.isFinite(delta) && direction !== "flat" && (
        <span>{delta > 0 ? "+" : ""}{delta.toFixed(1)}</span>
      )}
    </span>
  );
}


// Iter177 — Quick reaction chips a student can tap on any recap card so
// the coach knows the player read + appreciated the feedback. The BE
// whitelists these exact codepoints — keep this list in sync with
// _RECAP_REACTION_EMOJIS in server.py.
const REACTION_EMOJIS = ["🙏", "💪", "🔥", "❤️", "👍"];

function RecapReactionRow({ studentId, feedbackId, initial, onSaved }) {
  const [reaction, setReaction] = useState(initial || null);
  const [busy, setBusy] = useState(false);
  const [notePickerOpen, setNotePickerOpen] = useState(false);

  const send = async (payload) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/students/${studentId}/post-session-feedback/${feedbackId}/reaction`,
        payload,
      );
      setReaction(data.reaction);
      onSaved?.(data.reaction);
      toast.success("Coach notified — nice one");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reaction failed");
    } finally {
      setBusy(false);
      setNotePickerOpen(false);
    }
  };

  if (reaction && (reaction.emoji || reaction.note)) {
    return (
      <div
        className="flex items-center gap-2 text-xs text-[#A3A3A3]"
        data-testid={`recap-reaction-saved-${feedbackId}`}
      >
        <Heart className="w-3.5 h-3.5 text-[#CCFF00]" strokeWidth={2.5} />
        <span>
          You reacted{reaction.emoji ? ` ${reaction.emoji}` : ""}
          {reaction.note ? ` — “${reaction.note}”` : ""} · coach notified
        </span>
        <button
          type="button"
          onClick={() => setReaction(null)}
          data-testid={`recap-reaction-change-${feedbackId}`}
          className="ml-1 text-[10px] uppercase tracking-wider text-[#525252] hover:text-white"
        >
          Change
        </button>
      </div>
    );
  }

  return (
    <div
      className="flex flex-wrap items-center gap-1.5"
      data-testid={`recap-reaction-picker-${feedbackId}`}
    >
      <span className="text-[10px] uppercase tracking-wider text-[#525252] mr-1">
        Tap to thank your coach:
      </span>
      {REACTION_EMOJIS.map((em) => (
        <button
          key={em}
          type="button"
          disabled={busy}
          onClick={() => send({ emoji: em })}
          data-testid={`recap-reaction-emoji-${em}`}
          className="w-8 h-8 flex items-center justify-center border border-[#262626] hover:border-[#CCFF00] transition-colors text-lg disabled:opacity-40"
        >
          {em}
        </button>
      ))}
      <button
        type="button"
        disabled={busy}
        onClick={() => send({ note: "Thanks coach" })}
        data-testid={`recap-reaction-thanks-${feedbackId}`}
        className="px-3 h-8 text-[10px] uppercase tracking-wider border border-[#262626] hover:border-[#CCFF00] hover:text-[#CCFF00] disabled:opacity-40"
      >
        Thanks coach
      </button>
    </div>
  );
}

export default function ProgressGlance({ studentId }) {
  const [snapshots, setSnapshots] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [lastBookingType, setLastBookingType] = useState(null);  // iter161 — 'one_on_one' | 'group' | 'club'
  const [suggestedDrills, setSuggestedDrills] = useState([]);      // iter161
  const [requestedDrills, setRequestedDrills] = useState({});      // iter162 — {drill_id: true}
  const [requestingId, setRequestingId] = useState("");            // iter162
  const [expanded, setExpanded] = useState(false);
  // Iter176 — bell notifications now deep-link to /progress#recap-{id}. If
  // the hash matches the latest recap we highlight the hero; if it matches
  // an older entry we auto-expand the timeline and scroll to it. Also
  // flash a Volt outline for 2s so the player sees exactly what's new.
  const location = useLocation();
  const [highlightedRecapId, setHighlightedRecapId] = useState(null);

  useEffect(() => {
    if (!studentId) return;
    api.get(`/students/${studentId}/skill-snapshots`)
      .then((r) => setSnapshots(r.data?.entries || []))
      .catch(() => setSnapshots([]));
    api.get(`/students/${studentId}/post-session-feedback`)
      .then((r) => setFeedback(r.data?.items || []))
      .catch(() => setFeedback([]));
    // Iter161 — pull the student's most recent booking to default the CTA
    // "type" (solo / group / club) to whatever they last chose. Falls back
    // to solo when the student has no booking history yet.
    api.get(`/bookings`)
      .then((r) => {
        const items = Array.isArray(r.data) ? r.data : [];
        if (!items.length) return;
        // /bookings sorts by start ascending; grab the newest.
        const latest = items[items.length - 1];
        setLastBookingType(latest?.session_type || null);
      })
      .catch(() => {});
    // Iter162 — hydrate the "already requested" state for the drill cards
    // so a page refresh doesn't let the player double-request.
    api.get(`/students/${studentId}/drill-requests`)
      .then((r) => {
        const rows = r.data?.items || [];
        const map = {};
        rows.forEach((it) => { if (it.drill_id) map[it.drill_id] = true; });
        setRequestedDrills(map);
      })
      .catch(() => {});
  }, [studentId]);

  const latest = (snapshots || [])[0] || null;
  const latestFeedback = (feedback || [])[0] || null;
  const latestFeedbackId = latestFeedback?.id || null;

  // Iter176 — scroll-to-recap driver. Runs when the hash changes AND when
  // the underlying data is ready (so the target element is actually in the
  // DOM). If the hash points at an older recap, expand the timeline first
  // so the entry becomes reachable.
  useEffect(() => {
    if (!location.hash || !feedback || !snapshots) return;
    const m = location.hash.match(/^#recap-(.+)$/);
    if (!m) return;
    const recapId = m[1];
    const isLatest = latestFeedbackId && recapId === latestFeedbackId;
    if (!isLatest) setExpanded(true);
    setHighlightedRecapId(recapId);
    // Slight delay lets the newly-expanded timeline render before we scroll.
    const t = setTimeout(() => {
      const el = document.getElementById(`recap-${recapId}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, isLatest ? 60 : 220);
    // Fade the outline after 3s so the highlight doesn't linger forever.
    const t2 = setTimeout(() => setHighlightedRecapId(null), 3200);
    return () => { clearTimeout(t); clearTimeout(t2); };
  }, [location.hash, feedback, snapshots, latestFeedbackId]);
  const topFocus = useMemo(() => {
    const raw = (latest?.focus_areas || latestFeedback?.focus_areas_for_improvement || []).slice(0, 3);
    return raw;
  }, [latest, latestFeedback]);
  const coachId = latestFeedback?.coach_id;

  // Iter161 — map the last booking's session_type onto the /group-bookings/new
  // ?type= param so "Book Your Next Focus Session" defaults to how they
  // usually train (1:1 → solo, group → group, club → club).
  const defaultType = (() => {
    if (lastBookingType === "one_on_one") return "solo";
    if (lastBookingType === "group") return "group";
    if (lastBookingType === "club") return "club";
    return "solo";
  })();

  const bookFocusHref = coachId
    ? `/group-bookings/new?type=${defaultType}${coachId ? `&coach=${coachId}` : ""}${topFocus.length ? `&focus=${encodeURIComponent(topFocus.slice(0, 2).join(","))}` : ""}`
    : `/request/new`;
  const bringPartnerHref = coachId
    ? `/group-bookings/new?type=group${coachId ? `&coach=${coachId}` : ""}${topFocus.length ? `&focus=${encodeURIComponent(topFocus.slice(0, 2).join(","))}` : ""}`
    : `/request/new`;

  // Iter161 — Suggested drills for the next session. Match snapshot slugs
  // that appear in the focus bullets (same technique as detectTrend) and
  // fan out one /drills query per matched slug, then dedupe. Keeps the
  // fetch quiet if no focus areas or snapshot data yet.
  useEffect(() => {
    if (!latest || !topFocus.length) { setSuggestedDrills([]); return; }
    const snapKeys = Object.keys(latest.snapshot || {});
    const matchedSlugs = new Set();
    for (const bullet of topFocus) {
      const text = String(bullet).toLowerCase();
      const sorted = [...snapKeys].sort((a, b) => b.length - a.length);
      for (const k of sorted) {
        const humanised = k.replace(/_/g, " ").toLowerCase();
        const stem = humanised.endsWith("s") ? humanised.slice(0, -1) : humanised;
        if (text.includes(humanised) || (stem.length > 3 && text.includes(stem))) {
          matchedSlugs.add(k);
          break;
        }
      }
    }
    if (matchedSlugs.size === 0) { setSuggestedDrills([]); return; }
    Promise.all([...matchedSlugs].slice(0, 3).map((sk) =>
      api.get("/drills", { params: { sport: "pickleball", sub_skill: sk } })
        .then((r) => (r.data || []).slice(0, 2))
        .catch(() => [])
    )).then((batches) => {
      const seen = new Set();
      const merged = [];
      let more = true, i = 0;
      while (more) {
        more = false;
        for (const list of batches) {
          if (list[i] && !seen.has(list[i].id)) {
            seen.add(list[i].id);
            merged.push(list[i]);
            more = true;
          }
        }
        i += 1;
        if (merged.length >= 3) break;
      }
      setSuggestedDrills(merged);
    });
  }, [latest, topFocus.join("|")]);

  if (snapshots === null || feedback === null) {
    // Silent load — the parent MyProgress page already renders the hero header
    return (
      <div className="card-surface p-8 text-center text-sm text-[#A3A3A3]" data-testid="progress-glance-loading">
        Loading your progress…
      </div>
    );
  }

  // Empty state — no snapshots yet. Iter162 — 3-step "how your progress
  // will fill in" walkthrough so brand-new students know what to expect.
  if (!latest) {
    const steps = [
      { n: 1, title: "Book your first session", body: "Pick a coach and a time — Solo or Group both work." },
      { n: 2, title: "Play and get rated", body: "Your coach logs notes right after the session on the skills they saw." },
      { n: 3, title: "Your progress appears here", body: "Spider chart, focus areas, drill suggestions and a full timeline — all in one glance." },
    ];
    return (
      <div className="card-surface p-6 sm:p-8 space-y-6" data-testid="progress-glance-empty">
        <div className="text-center space-y-2">
          <Sparkles className="w-6 h-6 mx-auto text-[#CCFF00]" />
          <div className="h-display text-2xl">Your first snapshot is one session away.</div>
          <p className="text-sm text-[#A3A3A3] max-w-md mx-auto">
            Here's how this page will fill in once you play your first session with a coach.
          </p>
        </div>
        <ol className="grid gap-3 sm:grid-cols-3" data-testid="progress-glance-walkthrough">
          {steps.map((s) => (
            <li
              key={s.n}
              data-testid={`walkthrough-step-${s.n}`}
              className="bg-[#0A0A0A] border border-[#262626] p-4 space-y-2"
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center h-display text-lg"
                style={{ background: "#CCFF00", color: "#0A0A0A" }}
                aria-hidden
              >
                {s.n}
              </div>
              <div className="text-sm font-semibold">{s.title}</div>
              <div className="text-xs text-[#A3A3A3] leading-relaxed">{s.body}</div>
            </li>
          ))}
        </ol>
        <div className="flex justify-center">
          <Link
            to="/request/new"
            data-testid="progress-glance-request-first-session"
            className="inline-flex items-center gap-2 py-3 px-5 text-xs uppercase tracking-wider font-bold"
            style={{ background: "#CCFF00", color: "#0A0A0A" }}
          >
            <CalendarClock className="w-4 h-4" /> Book your first session
          </Link>
        </div>
      </div>
    );
  }

  const requestDrill = async (drill) => {
    if (requestedDrills[drill.id] || requestingId === drill.id) return;
    setRequestingId(drill.id);
    try {
      const { data } = await api.post(`/students/${studentId}/drill-requests`, {
        drill_id: drill.id,
        drill_title: drill.title || "",
        duration_min: drill.duration_min,
        coach_id: coachId || undefined,
      });
      setRequestedDrills((prev) => ({ ...prev, [drill.id]: true }));
      if (data?.already_requested) {
        toast.info("Already in your next session");
      } else {
        toast.success(`Queued: ${drill.title || "drill"} — coach notified`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not queue drill");
    } finally { setRequestingId(""); }
  };

  return (
    <div className="space-y-4" data-testid="progress-glance">
      {/* ---------------- GLANCE HERO ---------------- */}
      <div
        className="card-surface p-6 sm:p-8 transition-all"
        id={latestFeedbackId ? `recap-${latestFeedbackId}` : undefined}
        data-testid="progress-glance-hero"
        style={latestFeedbackId && highlightedRecapId === latestFeedbackId
          ? { boxShadow: "0 0 0 2px #CCFF00" }
          : undefined}
      >
        <div className="grid gap-6 md:grid-cols-[240px_1fr] items-center">
          <div className="flex justify-center md:justify-start" data-testid="progress-glance-spider-wrap">
            <Spider snapshot={latest.snapshot || {}} size={220} />
          </div>

          <div className="space-y-4 min-w-0">
            <div>
              <div className="label-mono text-[#CCFF00]">LATEST SESSION</div>
              <div className="text-[11px] uppercase tracking-wider text-[#A3A3A3]" data-testid="progress-glance-latest-date">
                {fmtDate(latest.session_start || latest.submitted_at)}
                {latest.session_title ? ` · ${latest.session_title}` : ""}
              </div>
            </div>

            {latest.summary ? (
              <p
                className="text-[15px] leading-relaxed text-[#E5E5E5]"
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
                data-testid="progress-glance-summary"
              >
                {latest.summary}
              </p>
            ) : (
              <p className="text-sm text-[#A3A3A3] italic">Your coach hasn't added a summary for this session yet.</p>
            )}

            {topFocus.length > 0 && (
              <div data-testid="progress-glance-focus-list">
                <div className="label-mono mb-1.5 text-[#A3A3A3]">Focus next</div>
                <ul className="space-y-1">
                  {topFocus.map((f) => {
                    const trend = detectTrend(f, snapshots || []);
                    return (
                      <li
                        key={f}
                        className="flex items-start gap-2 text-sm text-[#E5E5E5]"
                        data-testid="progress-glance-focus-item"
                      >
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "#FF9F1C" }} aria-hidden />
                        <span className="flex-1">{humanise(f)}</span>
                        <TrendArrow trend={trend} />
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            <div className="pt-1">
              <Link
                to={bookFocusHref}
                data-testid="book-focus-session-cta"
                className="inline-flex items-center gap-2 py-3 px-5 text-xs uppercase tracking-wider font-bold"
                style={{ background: "#CCFF00", color: "#0A0A0A" }}
              >
                <CalendarClock className="w-4 h-4" />
                Book Your Next Focus Session
              </Link>
              {lastBookingType && (
                <div className="text-[10px] text-[#525252] uppercase tracking-wider mt-1.5" data-testid="book-focus-default-type-hint">
                  Defaulting to {defaultType === "solo" ? "1:1" : defaultType} · same as your last session
                </div>
              )}
            </div>

            {/* Iter177 — Recap reaction chips on the hero (latest recap). */}
            {latestFeedbackId && (
              <div className="pt-3 border-t border-[#262626]">
                <RecapReactionRow
                  studentId={studentId}
                  feedbackId={latestFeedbackId}
                  initial={latestFeedback?.reaction || null}
                  onSaved={(r) => {
                    setFeedback((cur) => {
                      if (!Array.isArray(cur) || !cur.length) return cur;
                      const [head, ...rest] = cur;
                      return [{ ...head, reaction: r }, ...rest];
                    });
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Iter161 — Suggested drills for next session. Uses the same slug-match
          used by the trend arrows: for each focus bullet we pick the matched
          snapshot key and query the drill library. Shows up to 3 drills so
          the coach walks into the next session with warm-up material queued. */}
      {suggestedDrills.length > 0 && (
        <div className="card-surface p-6" data-testid="suggested-drills-card">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div>
              <div className="label-mono text-[#CCFF00] flex items-center gap-1.5">
                <BookOpen className="w-3.5 h-3.5" /> DRILLS FOR NEXT SESSION
              </div>
              <div className="text-xs text-[#A3A3A3] mt-0.5">
                Warm-up ideas matched to your focus areas — bring these to your coach.
              </div>
            </div>
            <Link
              to="/drills"
              data-testid="suggested-drills-see-all"
              className="text-[10px] uppercase tracking-wider text-[#A3A3A3] hover:text-[#CCFF00] inline-flex items-center gap-1"
            >
              Full library <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="grid gap-2 sm:grid-cols-3">
            {suggestedDrills.map((d) => {
              const isQueued = !!requestedDrills[d.id];
              const isPending = requestingId === d.id;
              return (
                <div
                  key={d.id}
                  data-testid={`suggested-drill-${d.id}`}
                  className="bg-[#0A0A0A] border border-[#262626] p-3 flex flex-col gap-2 hover:border-[#CCFF00] transition-colors"
                  style={{ borderColor: isQueued ? "#CCFF00" : undefined }}
                >
                  <Link to={`/drills/${d.id}`} className="min-w-0 block">
                    <div className="text-sm font-semibold line-clamp-2">{d.title}</div>
                    <div className="text-[10px] uppercase tracking-wider text-[#A3A3A3] mt-1">
                      {(d.duration_min || 10)}min
                      {(d.sub_skills || []).slice(0, 2).map((s) => (
                        <span key={s} className="ml-1.5">· {s.replace(/_/g, " ")}</span>
                      ))}
                    </div>
                  </Link>
                  <button
                    type="button"
                    onClick={() => requestDrill(d)}
                    disabled={isQueued || isPending}
                    data-testid={`queue-drill-${d.id}`}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-wider border transition-colors disabled:cursor-default"
                    style={
                      isQueued
                        ? { background: "#CCFF00", color: "#0A0A0A", borderColor: "#CCFF00" }
                        : { borderColor: "#404040", color: "#A3A3A3" }
                    }
                  >
                    {isQueued
                      ? (<><Check className="w-3 h-3" /> Queued for next session</>)
                      : isPending
                        ? "Queuing…"
                        : (<><Plus className="w-3 h-3" /> Add to next session</>)}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ---------------- SEE MORE TOGGLE ---------------- */}
      <div className="flex justify-center">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid="progress-glance-toggle"
          aria-expanded={expanded}
          aria-controls="progress-glance-expanded"
          className="inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-white border border-[#262626] hover:border-[#404040] transition-colors"
        >
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {expanded ? "Hide full history" : "View full history"}
        </button>
      </div>

      {/* ---------------- EXPANDED HISTORY ---------------- */}
      {expanded && (
        <div className="space-y-3" id="progress-glance-expanded" data-testid="progress-glance-expanded">
          <div className="card-surface p-6" data-testid="progress-glance-partner-card">
            <div className="flex items-start gap-4 flex-wrap">
              <div className="flex-1 min-w-[220px] space-y-1">
                <div className="label-mono text-[#CCFF00] flex items-center gap-2">
                  <Users className="w-3.5 h-3.5" /> BRING A TRAINING PARTNER
                </div>
                <p className="text-sm text-[#A3A3A3]">
                  Invite a mate to a Group session on your current focus areas — same coach, split the cost, work on the same skills.
                </p>
              </div>
              <Link
                to={bringPartnerHref}
                data-testid="bring-training-partner-cta"
                className="inline-flex items-center gap-2 py-3 px-5 text-xs uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black transition-colors"
              >
                <CalendarClock className="w-4 h-4" />
                Invite a partner
              </Link>
            </div>
          </div>

          {/* Session-by-session log. Uses feedback (per-skill notes)
              cross-joined with snapshots (spider) when a feedback entry
              carries a session_id match. Falls back to snapshot-only rows. */}
          {(feedback.length ? feedback : snapshots).map((entry) => {
            const isFeedback = "per_skill_notes" in entry;
            const snap = isFeedback
              ? (snapshots.find((s) => s.feedback_id === entry.id)?.snapshot || entry.skill_snapshot || {})
              : (entry.snapshot || {});
            const summary = isFeedback ? entry.ai_summary : entry.summary;
            const focus = isFeedback
              ? (entry.focus_areas_for_improvement || [])
              : (entry.focus_areas || []);
            const perSkill = isFeedback ? (entry.per_skill_notes || {}) : {};
            const stamp = isFeedback ? entry.session_start : (entry.session_start || entry.submitted_at);
            const title = (isFeedback ? entry.session_title : entry.session_title) || "Session";
            const key = isFeedback ? entry.id : entry.feedback_id;
            return (
              <div
                key={key}
                id={key ? `recap-${key}` : undefined}
                className="card-surface p-5 sm:p-6 space-y-3 transition-all"
                data-testid={`progress-history-entry-${key}`}
                style={key && highlightedRecapId === key
                  ? { boxShadow: "0 0 0 2px #CCFF00" }
                  : undefined}
              >
                <div className="flex items-baseline justify-between flex-wrap gap-2">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-[#A3A3A3]">{fmtDate(stamp)}</div>
                    <div className="text-sm font-semibold">{title}</div>
                  </div>
                  {focus.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {focus.map((f) => (
                        <span key={f} className="text-[10px] uppercase tracking-wider px-2 py-0.5 border border-[#FF9F1C] text-[#FF9F1C]">
                          {humanise(f)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {summary && (
                  <p className="text-sm text-[#E5E5E5] leading-relaxed">{summary}</p>
                )}

                {Object.keys(perSkill).length > 0 && (
                  <div className="space-y-1.5 pt-1 border-t border-[#262626]" data-testid={`progress-history-notes-${key}`}>
                    <div className="label-mono text-[#A3A3A3]">Per-skill notes</div>
                    {Object.entries(perSkill).map(([slug, note]) => (
                      <div key={slug} className="text-xs text-[#E5E5E5]">
                        <span className="text-[#A3A3A3]">{humanise(slug)} — </span>
                        <span>{note}</span>
                      </div>
                    ))}
                  </div>
                )}

                {Object.keys(snap).length >= 3 && (
                  <div className="flex justify-center pt-1">
                    <Spider snapshot={snap} size={180} color="#66C2FF" />
                  </div>
                )}

                {/* Iter177 — Reaction row on every historical recap the
                    student can react to. Snapshot-only rows (no feedback_id)
                    render nothing. */}
                {isFeedback && key && (
                  <div className="pt-2 border-t border-[#262626]">
                    <RecapReactionRow
                      studentId={studentId}
                      feedbackId={key}
                      initial={entry.reaction || null}
                      onSaved={(r) => {
                        setFeedback((cur) => (Array.isArray(cur)
                          ? cur.map((it) => (it.id === key ? { ...it, reaction: r } : it))
                          : cur));
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
